import { type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'
import { cn } from '@/lib/utils'

/* shared right-side sheet for short create/edit forms (add document, invite
 * member, etc.). encapsulates the one tricky bit: it runs modal={false} so
 * popups that portal to the body — combobox, date picker — stay clickable
 * inside it (a modal sheet traps pointer events to its own subtree, leaving
 * those popups visible but dead). modal={false} renders no overlay, so we add
 * our own backdrop that dims and closes on click. callers own the open state
 * (so they can close on success) and just supply the trigger + form body. */
export function FormSheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  footer,
  children,
  contentClassName
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  footer?: ReactNode
  children: ReactNode
  contentClassName?: string
}) {
  useBodyScrollLock(open)

  return (
    <>
      {open
        ? createPortal(
            <div className='fixed inset-0 z-40 bg-black/40' />,
            document.body
          )
        : null}

      <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
        {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
        <SheetContent className={cn('sm:max-w-xl', contentClassName)}>
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            {description ? (
              <SheetDescription>{description}</SheetDescription>
            ) : null}
          </SheetHeader>

          <div className='flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-4 sm:px-6'>
            {children}
          </div>

          {footer ? <SheetFooter>{footer}</SheetFooter> : null}
        </SheetContent>
      </Sheet>
    </>
  )
}
